import { useMemo, useRef } from 'react'
import { SESSION_LOCATION } from '@fs/zion-rootstech'
import { useFeatureFlag } from '@fs/zion-flags'
import useCalendarData from './useCalendarData'
import { roundTimestampDown } from '../../../lib/helpers'
import scheduleConfig from '../../../config/schedule'
import { NEW_CALENDAR_FLAG } from '../../../config/feature/flags'

export default function useScheduleData({ skip = false } = {}) {
  const newCalendarFlag = useFeatureFlag(NEW_CALENDAR_FLAG)
  const { calendars } = scheduleConfig.calendarConfig

  // Use multi-stage calendar when flag is 'on', otherwise use main-stage + sessions
  const useNewCalendar = Boolean(newCalendarFlag?.isOn)

  // Determine which calendars to fetch based on flag
  const shouldFetchMainStage = !useNewCalendar
  const shouldFetchSessions = !useNewCalendar
  const shouldFetchMultiStage = useNewCalendar

  const { loading: mainStageLoading, data: mainStageData } = useCalendarData({
    calendarType: calendars.mainStage,
    skip: skip || !shouldFetchMainStage,
  })
  const { loading: sessionsLoading, data: sessionsData } = useCalendarData({
    calendarType: calendars.sessions,
    skip: skip || !shouldFetchSessions,
  })
  const { loading: multiStageLoading, data: multiStageData } = useCalendarData({
    calendarType: calendars.multiStage,
    skip: skip || !shouldFetchMultiStage,
  })

  const loading = mainStageLoading || sessionsLoading || multiStageLoading

  // Track whether we've ever received data to differentiate initial load from refetch
  const hasReceivedDataRef = useRef(false)
  if (!hasReceivedDataRef.current && !loading) {
    hasReceivedDataRef.current = true
  }

  const data = useMemo(() => {
    // On initial load while loading, return empty to prevent partial schedule rendering
    // On refetch, process cached data to avoid flicker (Apollo preserves previous results)
    if (loading && !hasReceivedDataRef.current) {
      return []
    }

    // Merge and normalize all calendar sources
    const merged = [...(mainStageData ?? []), ...(sessionsData ?? []), ...(multiStageData ?? [])]
      // BS saves seconds when a session is created. This rounds down the timestamps to the nearest minute so the sessions always start at 00 seconds
      .map(({ date, ...rest }) => ({
        date: roundTimestampDown({ to: 'minute', time: date }),
        ...rest,
      }))
      .sort((a, b) => a.date - b.date)

    // O(n) deduplication and rebroadcast detection using reduce + Map/Set
    // Track: 1) first occurrence by contentId, 2) unique date+contentId pairs
    const seenByContentId = new Map() // contentId -> first occurrence date
    const seenDuplicates = new Set() // `${date}:${contentId}` for exact duplicates

    return merged.reduce((acc, calendarItem) => {
      if (!calendarItem?.item?.contentId) return acc

      const { date, item } = calendarItem
      const { contentId } = item
      const duplicateKey = `${date}:${contentId}`

      // Skip exact duplicates (same date + contentId)
      if (seenDuplicates.has(duplicateKey)) return acc
      seenDuplicates.add(duplicateKey)

      // Check if this is a rebroadcast (same contentId, later date)
      const firstOccurrenceDate = seenByContentId.get(contentId)
      if (firstOccurrenceDate !== undefined && firstOccurrenceDate < date) {
        // This is a rebroadcast - mark as online replay
        acc.push({
          date,
          item: { ...item, sessionLocation: SESSION_LOCATION.online, replay: true },
        })
      } else {
        // First occurrence or not a rebroadcast
        if (firstOccurrenceDate === undefined) {
          seenByContentId.set(contentId, date)
        }
        acc.push(calendarItem)
      }

      return acc
    }, [])
  }, [loading, mainStageData, sessionsData, multiStageData])

  return { loading, data }
}
